import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import '../styles/design-system.css';
import '../styles/Dashboard.css';

function BrandForm({ mode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(mode === 'edit');
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '', niche: '', tone: '', target_audience: '', forbidden: '', logo: null,
  });
  const [currentLogo, setCurrentLogo] = useState(null);

  const resolveMediaUrl = (path) => {
    if (!path) return path;
    if (/^https?:\/\//i.test(path)) return path;
    const apiBase = (api.defaults.baseURL || window.location.origin).replace(/\/$/, '');
    return `${apiBase}${path.startsWith('/') ? path : `/${path}`}`;
  };

  useEffect(() => {
    if (mode === 'edit' && id) fetchBrand();
  }, [mode, id]);

  const fetchBrand = async () => {
    try {
      const response = await api.get(`/api/brands/${id}`);
      const brand = response.data;
      setFormData({ name: brand.name || '', niche: brand.niche || '', tone: brand.tone || '',
        target_audience: brand.target_audience || '', forbidden: brand.forbidden || '', logo: null });
      setCurrentLogo(brand.logo_path);
    } catch (error) {
      alert('Failed to load brand data');
      navigate('/dashboard');
    } finally { setLoading(false); }
  };

  const handleChange = (e) => {
    if (e.target.name === 'logo') {
      setFormData({ ...formData, logo: e.target.files[0] });
    } else {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const submitData = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && formData[key] !== '') submitData.append(key, formData[key]);
      });
      if (mode === 'edit') {
        await api.post(`/brand/${id}/update`, submitData, { headers: { 'Content-Type': 'multipart/form-data' } });
        alert('Brand updated!');
        navigate(`/brand/${id}`);
      } else {
        await api.post('/brand/create', submitData, { headers: { 'Content-Type': 'multipart/form-data' } });
        alert('Brand created!');
        navigate('/dashboard');
      }
    } catch (error) {
      alert('Failed to save brand: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="ps-loading">
        <div className="ps-spinner" />
        <span className="ps-loading-text">Loading brand…</span>
      </div>
    );
  }

  return (
    <div className="ps-page">
      {/* ── Navbar ── */}
      <nav className="ps-nav">
        <div className="ps-container">
          <div className="ps-nav__inner">
            <Link to="/dashboard" className="ps-nav__logo">
              Postrify <span className="ps-nav__logo-dot" />
            </Link>
            <div className="ps-nav__right">
              <Link to="/dashboard" className="ps-btn ps-btn--ghost ps-btn--sm">← Dashboard</Link>
              <button onClick={logout} className="ps-btn ps-btn--secondary ps-btn--sm">Log out</button>
            </div>
          </div>
        </div>
      </nav>

      <div className="ps-container">
        <div className="brand-form-wrap">
          <div className="ps-page-header" style={{paddingBottom:'20px'}}>
            <div>
              <h1 className="ps-page-header__title">
                {mode === 'create' ? 'Create New Brand' : 'Edit Brand'}
              </h1>
              <p className="ps-page-header__sub">
                {mode === 'create'
                  ? 'Configure your brand to start generating AI-powered content'
                  : 'Update your brand settings and preferences'}
              </p>
            </div>
          </div>

          <div className="ps-card">
            <form onSubmit={handleSubmit}>
              <div className="ps-field">
                <label className="ps-label" htmlFor="name">Brand Name *</label>
                <input className="ps-input" type="text" id="name" name="name" required
                  placeholder="e.g. EcoLife Products"
                  value={formData.name} onChange={handleChange} />
              </div>

              <div className="ps-field">
                <label className="ps-label" htmlFor="niche">Niche / Industry *</label>
                <input className="ps-input" type="text" id="niche" name="niche" required
                  placeholder="e.g. Sustainable Living, Fitness, Fashion"
                  value={formData.niche} onChange={handleChange} />
              </div>

              <div className="ps-field">
                <label className="ps-label" htmlFor="tone">Brand Tone *</label>
                <input className="ps-input" type="text" id="tone" name="tone" required
                  placeholder="e.g. professional, friendly, luxury, inspiring"
                  value={formData.tone} onChange={handleChange} />
              </div>

              <div className="ps-field">
                <label className="ps-label" htmlFor="target_audience">
                  Target Audience
                  <span style={{fontWeight:400,textTransform:'none',letterSpacing:0,color:'#8b8aab'}}> (Optional)</span>
                </label>
                <input className="ps-input" type="text" id="target_audience" name="target_audience"
                  placeholder="e.g. Millennials interested in sustainability"
                  value={formData.target_audience} onChange={handleChange} />
              </div>

              <div className="ps-field">
                <label className="ps-label" htmlFor="forbidden">
                  Forbidden Topics / Words
                  <span style={{fontWeight:400,textTransform:'none',letterSpacing:0,color:'#8b8aab'}}> (Optional)</span>
                </label>
                <input className="ps-input" type="text" id="forbidden" name="forbidden"
                  placeholder="e.g. politics, religion (comma-separated)"
                  value={formData.forbidden} onChange={handleChange} />
                <p className="ps-hint">Topics the AI should never include in generated content.</p>
              </div>

              <div className="ps-field">
                <label className="ps-label" htmlFor="logo">
                  Brand Logo
                  <span style={{fontWeight:400,textTransform:'none',letterSpacing:0,color:'#8b8aab'}}> (Optional)</span>
                </label>
                <input className="ps-input" type="file" id="logo" name="logo"
                  accept="image/*" onChange={handleChange} />
                <p className="ps-hint">Supported formats: JPG, PNG, GIF, WebP, SVG · Max 5 MB</p>

                {currentLogo && (
                  <div className="current-logo-preview">
                    <p>Current Logo</p>
                    <img src={resolveMediaUrl(currentLogo)} alt="Current brand logo" />
                  </div>
                )}
              </div>

              <div className="brand-form-actions">
                <button type="submit" className="ps-btn ps-btn--primary" disabled={submitting}>
                  {submitting ? 'Saving…' : (mode === 'create' ? 'Create Brand →' : 'Save Changes')}
                </button>
                <Link to={mode === 'edit' ? `/brand/${id}` : '/dashboard'} className="ps-btn ps-btn--secondary">
                  Cancel
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>

      <footer className="ps-footer">
        <div className="ps-container">
          © 2024 Postrify · <Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link>
        </div>
      </footer>
    </div>
  );
}

export default BrandForm;
